import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, BookOpen, Moon, Trash2, Plus, Loader2, Shield, RefreshCw, Check, Play, X } from 'lucide-react';
import SamtalFokusTraner from './SamtalFokusTraner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RITUAL_KINDS = [
  { id: 'gratitude',    label: 'Tacksamhet', emoji: '🌷', color: '#FFE8F5' },
  { id: 'intention',    label: 'Intention',  emoji: '🔮', color: '#C8B6FF' },
  { id: 'manifestation',label: 'Manifestera',emoji: '🌈', color: '#FFD6A5' },
  { id: 'cleansing',    label: 'Rensning',   emoji: '🕯️', color: '#FFE8A3' },
  { id: 'moon',         label: 'Måne',       emoji: '🌙', color: '#A2D2FF' },
  { id: 'other',        label: 'Annat',      emoji: '✨', color: '#E4D6F5' },
];

function MoonView() {
  const [moon, setMoon] = useState(null);
  useEffect(() => {
    axios.get(`${API}/vitmagi/moon`, { withCredentials: true })
      .then((r) => setMoon(r.data))
      .catch(() => {});
  }, []);
  if (!moon) return null;
  return (
    <section className="neo-card p-4 bg-gradient-to-br from-[#0F0F2D] to-[#3D2A5F] text-white" data-testid="vitmagi-moon">
      <p className="text-[10px] font-bold tracking-[0.3em] opacity-60 uppercase">Månens fas</p>
      <div className="flex items-center gap-3 mt-1">
        <span className="text-6xl">{moon.emoji}</span>
        <div>
          <p className="font-['Fredoka'] font-bold text-xl">{moon.swedish_name}</p>
          <p className="text-xs opacity-70">{moon.illumination_pct}% upplyst · dag {moon.age_days} av cykeln</p>
        </div>
      </div>
      <p className="mt-3 text-sm italic">💫 {moon.intention_suggestion}</p>
    </section>
  );
}

function LessonsView({ lessons }) {
  return (
    <div className="space-y-3" data-testid="vitmagi-lessons">
      {lessons.map((l) => (
        <section
          key={l.id}
          className="neo-card p-4 border-l-4"
          style={{ backgroundColor: `${l.color}88`, borderLeftColor: l.color }}
          data-testid={`vitmagi-lesson-${l.id}`}
        >
          <div className="flex items-baseline gap-2">
            <span className="text-2xl">{l.emoji}</span>
            <h3 className="font-['Fredoka'] font-bold text-lg">{l.n > 0 ? `${l.n}. ` : ''}{l.title}</h3>
          </div>
          <p className="text-sm font-bold opacity-80 mt-1 mb-2">{l.summary}</p>
          <p className="text-sm whitespace-pre-line leading-relaxed">{l.body}</p>
          {l.tips && (
            <ul className="mt-3 space-y-1 bg-white/60 rounded-md p-2">
              {l.tips.map((t, i) => <li key={i} className="text-xs opacity-80">🌸 {t}</li>)}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

function JournalView() {
  const [rituals, setRituals] = useState([]);
  const [creating, setCreating] = useState(false);
  const [kind, setKind] = useState('gratitude');
  const [intention, setIntention] = useState('');
  const [gratitude, setGratitude] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vitmagi/rituals`, { withCredentials: true });
      setRituals(r.data?.rituals || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!intention.trim() && !gratitude.trim() && !note.trim()) {
      toast.error('Skriv något 🌸');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/vitmagi/rituals`, {
        kind,
        intention: intention.trim() || null,
        gratitude: gratitude.trim() || null,
        note: note.trim() || null,
      }, { withCredentials: true });
      toast.success('Sparat ✨');
      setIntention(''); setGratitude(''); setNote('');
      setCreating(false);
      load();
    } catch { toast.error('Kunde inte spara'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort?')) return;
    try { await axios.delete(`${API}/vitmagi/rituals/${id}`, { withCredentials: true }); load(); }
    catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-3" data-testid="vitmagi-journal">
      {!creating && (
        <button
          onClick={() => setCreating(true)}
          className="neo-btn w-full py-3 bg-gradient-to-br from-[#FFE8F5] to-[#C8B6FF] font-bold flex items-center justify-center gap-1"
          data-testid="vitmagi-new-ritual"
        >
          <Plus className="w-4 h-4" /> Ny rituell anteckning
        </button>
      )}
      {creating && (
        <section className="neo-card p-3 bg-white space-y-2" data-testid="vitmagi-form">
          <p className="text-[11px] font-bold uppercase opacity-60">Ritualtyp</p>
          <div className="grid grid-cols-3 gap-1">
            {RITUAL_KINDS.map((k) => (
              <button
                key={k.id}
                type="button"
                onClick={() => setKind(k.id)}
                className={`p-2 rounded-md border-2 text-[10px] font-bold flex flex-col items-center gap-0.5 ${kind === k.id ? 'ring-2 ring-[#D06892]' : ''}`}
                style={{ backgroundColor: k.color, borderColor: kind === k.id ? '#D06892' : 'rgba(0,0,0,0.1)' }}
                data-testid={`vitmagi-kind-${k.id}`}
              >
                <span className="text-base">{k.emoji}</span>
                <span>{k.label}</span>
              </button>
            ))}
          </div>
          <textarea
            value={intention}
            onChange={(e) => setIntention(e.target.value)}
            placeholder="Min intention..."
            rows={2}
            className="neo-input w-full text-sm"
            data-testid="vitmagi-intention"
          />
          <textarea
            value={gratitude}
            onChange={(e) => setGratitude(e.target.value)}
            placeholder="Jag är tacksam för..."
            rows={2}
            className="neo-input w-full text-sm"
            data-testid="vitmagi-gratitude"
          />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anteckning..."
            rows={2}
            className="neo-input w-full text-sm"
            data-testid="vitmagi-note"
          />
          <div className="flex gap-2">
            <button onClick={() => setCreating(false)} className="neo-btn flex-1 py-2 bg-gray-100 text-sm">Avbryt</button>
            <button onClick={submit} disabled={busy} className="neo-btn flex-1 py-2 bg-[#B9FBC0] text-sm font-bold" data-testid="vitmagi-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara ✨'}
            </button>
          </div>
        </section>
      )}
      {rituals.length === 0 && !creating && (
        <p className="text-center text-xs opacity-60 italic py-6">Ingen ritualjournal än 🌷</p>
      )}
      {rituals.map((r) => {
        const meta = RITUAL_KINDS.find((k) => k.id === r.kind) || RITUAL_KINDS[5];
        return (
          <section
            key={r.id}
            className="neo-card p-3 border-l-4"
            style={{ backgroundColor: `${meta.color}88`, borderLeftColor: meta.color }}
            data-testid={`vitmagi-ritual-${r.id}`}
          >
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-sm font-bold flex items-center gap-1">
                <span>{meta.emoji}</span> {meta.label}
                {r.moon_phase?.emoji && <span className="opacity-60 ml-1">{r.moon_phase.emoji}</span>}
              </p>
              <button onClick={() => remove(r.id)} className="p-1 opacity-50 hover:opacity-100">
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            </div>
            {r.intention && <p className="text-sm">🔮 {r.intention}</p>}
            {r.gratitude && <p className="text-sm">🌷 {r.gratitude}</p>}
            {r.note && <p className="text-xs italic opacity-70 mt-1">{r.note}</p>}
            <p className="text-[9px] opacity-50 mt-1">{r.created_at?.slice ? r.created_at.slice(0, 10) : ''}</p>
          </section>
        );
      })}
    </div>
  );
}

function AuraRitualModal({ ritual, onClose }) {
  const [step, setStep] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ritual.steps[0]?.seconds || 15);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return undefined;
    if (timeLeft <= 0) {
      if (step < ritual.steps.length - 1) {
        setStep((s) => s + 1);
        setTimeLeft(ritual.steps[step + 1].seconds);
      } else {
        setRunning(false);
      }
      return undefined;
    }
    const id = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [running, timeLeft, step, ritual.steps]);

  const done = !running && step === ritual.steps.length - 1 && timeLeft <= 0;
  const current = ritual.steps[step];

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="aura-ritual-modal">
      <div className="bg-gradient-to-br from-[#1a0e3a] via-[#3d1e6b] to-[#5a2b8f] text-white rounded-3xl max-w-md w-full p-6 relative shadow-2xl border border-purple-300/30" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full bg-white/10 hover:bg-white/20" data-testid="aura-ritual-close" aria-label="Stäng">
          <X className="w-5 h-5" />
        </button>
        <div className="text-center">
          <Shield className="w-10 h-10 mx-auto text-purple-200 opacity-80" />
          <h2 className="text-2xl font-bold font-['Fredoka'] mt-2">{ritual.title}</h2>
          <p className="text-[11px] opacity-70 mt-0.5">{ritual.duration_min} minuter · {ritual.when_to_use}</p>
        </div>

        {!running && !done && (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-center opacity-90 italic">Sätt dig bekvämt. Andas normalt. Klicka starta när du är redo. ✨</p>
            <button
              onClick={() => { setRunning(true); setStep(0); setTimeLeft(ritual.steps[0].seconds); }}
              className="w-full py-3 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 text-white font-bold flex items-center justify-center gap-2 shadow-lg"
              data-testid="aura-ritual-start"
            >
              <Play className="w-4 h-4" /> Starta ritualen
            </button>
          </div>
        )}

        {running && current && (
          <div className="mt-5 text-center space-y-3" data-testid="aura-ritual-active">
            <div className="text-xs opacity-60 uppercase tracking-widest">Steg {current.n} / {ritual.steps.length}</div>
            <p className="text-lg font-['Fredoka'] leading-relaxed px-2">{current.text}</p>
            <div className="text-5xl font-bold tabular-nums" data-testid="aura-ritual-timer">{timeLeft}s</div>
            <button onClick={() => setRunning(false)} className="text-xs opacity-60 underline">Avbryt</button>
          </div>
        )}

        {done && (
          <div className="mt-5 text-center space-y-3" data-testid="aura-ritual-done">
            <div className="text-4xl">✨🕊️✨</div>
            <p className="text-sm italic opacity-90 leading-relaxed">{ritual.closing}</p>
            <button
              onClick={onClose}
              className="mt-2 w-full py-3 rounded-full bg-white/15 hover:bg-white/25 font-bold"
              data-testid="aura-ritual-finish"
            >
              Klar — jag bär skölden med mig 💜
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AuraView() {
  const [data, setData] = useState(null);
  const [ritualOpen, setRitualOpen] = useState(false);
  const [thoughtBusy, setThoughtBusy] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vitmagi/aura`, { withCredentials: true });
      setData(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rerollThought = async () => {
    setThoughtBusy(true);
    try {
      const r = await axios.post(`${API}/vitmagi/aura/thought/new`, {}, { withCredentials: true });
      if (r.data?.thought) setData((d) => ({ ...d, daily_thought: r.data.thought }));
    } catch { /* silent */ }
    setThoughtBusy(false);
  };

  const toggleChallenge = async () => {
    if (!data) return;
    const next = !data.weekly_challenge_done;
    try {
      await axios.post(`${API}/vitmagi/aura/challenge`, { done: next }, { withCredentials: true });
      setData((d) => ({ ...d, weekly_challenge_done: next }));
      if (next) toast.success('🌙 Grymt — du bygger mystiken denna vecka ✨');
    } catch { toast.error('Kunde inte spara'); }
  };

  if (!data) return <div className="text-center py-8 opacity-50 text-xs">Laddar din aura... ✨</div>;

  return (
    <div className="space-y-4" data-testid="vitmagi-aura">
      {/* Daily energy thought */}
      <section className="neo-card p-5 bg-gradient-to-br from-[#FFE8F5] via-[#FFF7E6] to-[#C8B6FF] relative overflow-hidden" data-testid="aura-daily-thought">
        <div className="absolute top-2 right-2">
          <button
            onClick={rerollThought}
            disabled={thoughtBusy}
            className="p-1.5 rounded-full bg-white/60 hover:bg-white text-purple-700 shadow-sm"
            title="Ny tanke"
            data-testid="aura-thought-reroll"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${thoughtBusy ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Dagens energi-tanke ⚡</div>
        <p className="mt-2 text-base leading-relaxed font-['Fredoka'] italic text-purple-900 pr-8">
          &ldquo;{data.daily_thought}&rdquo;
        </p>
      </section>

      {/* Weekly mystique challenge */}
      <section className="neo-card p-4 bg-gradient-to-r from-[#0F0F2D] to-[#3D2A5F] text-white" data-testid="aura-weekly-challenge">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Veckans mystik-utmaning 🌙</div>
            <h3 className="text-lg font-bold font-['Fredoka'] mt-0.5">{data.weekly_challenge.title}</h3>
            <p className="text-xs opacity-85 mt-1 leading-relaxed">{data.weekly_challenge.detail}</p>
          </div>
          <button
            onClick={toggleChallenge}
            data-testid="aura-challenge-toggle"
            className={`flex-shrink-0 w-11 h-11 rounded-full border-2 flex items-center justify-center transition ${data.weekly_challenge_done ? 'bg-gradient-to-br from-emerald-400 to-teal-500 border-emerald-200' : 'bg-white/10 border-white/30 hover:bg-white/20'}`}
            title={data.weekly_challenge_done ? 'Klart denna vecka ✨' : 'Markera som gjort'}
          >
            {data.weekly_challenge_done ? <Check className="w-5 h-5" /> : <span className="text-[10px] opacity-60">markera</span>}
          </button>
        </div>
      </section>

      {/* 3-min protective aura ritual button */}
      <button
        onClick={() => setRitualOpen(true)}
        className="w-full neo-btn py-3.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold flex items-center justify-center gap-2 shadow-md"
        data-testid="aura-open-ritual"
      >
        <Shield className="w-4 h-4" /> Kör &ldquo;Skyddande aura&rdquo;-ritualen (3 min)
      </button>

      {/* Techniques library — collapsible categories */}
      <div className="space-y-2" data-testid="aura-techniques">
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 pl-1">Tekniker — bli den mystiska magneten</p>
        {data.techniques.map((cat) => {
          const isOpen = openCategory === cat.category;
          return (
            <section key={cat.category} className="neo-card overflow-hidden" style={{ backgroundColor: `${cat.color}66` }} data-testid={`aura-cat-${cat.category}`}>
              <button
                onClick={() => setOpenCategory(isOpen ? null : cat.category)}
                className="w-full p-3 flex items-center justify-between text-left hover:bg-white/20"
                data-testid={`aura-cat-toggle-${cat.category}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{cat.emoji}</span>
                  <div>
                    <div className="font-bold text-sm">{cat.category}</div>
                    <div className="text-[10px] opacity-70 italic">{cat.why}</div>
                  </div>
                </div>
                <span className="text-xs opacity-60">{isOpen ? '▲' : '▼'}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2">
                  {cat.items.map((item, i) => (
                    <div key={i} className="bg-white/80 rounded-xl p-2.5 border border-white/60">
                      <div className="text-xs font-bold text-purple-900">{i + 1}. {item.title}</div>
                      <div className="text-[11px] text-gray-700 mt-0.5 leading-relaxed">{item.how}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {ritualOpen && <AuraRitualModal ritual={data.ritual} onClose={() => setRitualOpen(false)} />}
    </div>
  );
}

function ReadingFocusView() {
  const [data, setData] = useState(null);
  const [openCat, setOpenCat] = useState(null);

  useEffect(() => {
    axios.get(`${API}/vitmagi/reading-strategies`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => {/* silent */});
  }, []);

  if (!data) return <div className="text-center py-6 opacity-50 text-xs">Laddar strategier... ✨</div>;

  return (
    <div className="space-y-3" data-testid="vitmagi-reading">
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFE8A3] via-[#B9FBC0] to-[#A2D2FF]">
        <div className="flex items-center gap-2">
          <BookOpen className="w-6 h-6" />
          <h2 className="text-lg font-bold font-['Fredoka']">Läs & minns</h2>
        </div>
        <p className="text-[11px] italic opacity-80 mt-1 leading-relaxed">{data.intro}</p>
      </section>

      {data.categories.map((cat) => {
        const isOpen = openCat === cat.category;
        return (
          <section key={cat.category} className="neo-card overflow-hidden" style={{ backgroundColor: `${cat.color}66` }}>
            <button
              onClick={() => setOpenCat(isOpen ? null : cat.category)}
              className="w-full p-3 flex items-center justify-between text-left hover:bg-white/20"
              data-testid={`reading-cat-${cat.category}`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-2xl">{cat.emoji}</span>
                <div>
                  <div className="font-bold text-sm">{cat.category}</div>
                  <div className="text-[10px] opacity-70 italic">{cat.why}</div>
                </div>
              </div>
              <span className="text-xs opacity-60">{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div className="px-3 pb-3 space-y-2">
                {cat.items.map((item, i) => (
                  <div key={i} className="bg-white/85 rounded-xl p-2.5 border border-white/60">
                    <div className="text-xs font-bold text-purple-900">{item.title}</div>
                    <div className="text-[11px] text-gray-700 mt-0.5 leading-relaxed">{item.how}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default function VitMagiTab() {
  const [subtab, setSubtab] = useState('moon');
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    axios.get(`${API}/vitmagi/lessons`, { withCredentials: true })
      .then((r) => setLessons(r.data?.lessons || []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-3" data-testid="vitmagi-tab">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFF7E6] via-[#FFE8F5] to-[#C8B6FF]">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Vit magi</p>
        <h1 className="font-['Fredoka'] font-bold text-2xl mt-0.5">Lite mjuk magi ✨</h1>
        <p className="text-xs opacity-70 mt-1">Inte trolldom — snäll praktik för intention, tacksamhet och rytm.</p>
      </header>

      <div className="flex gap-1.5 flex-wrap" data-testid="vitmagi-subtabs">
        {[
          { id: 'moon', label: 'Idag', emoji: '🌙' },
          { id: 'aura', label: 'Bli mitt bästa jag', emoji: '💎' },
          { id: 'samtal', label: 'Samtals-fokus', emoji: '🎯' },
          { id: 'reading', label: 'Läs & minns', emoji: '📚' },
          { id: 'journal', label: 'Journal', emoji: '📓' },
          { id: 'lessons', label: 'Lär dig', emoji: '📖' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 ${subtab === t.id ? 'bg-[#C8B6FF]' : 'bg-white'}`}
            data-testid={`vitmagi-subtab-${t.id}`}
          >
            <span>{t.emoji}</span> {t.label}
          </button>
        ))}
      </div>

      {subtab === 'moon' && <MoonView />}
      {subtab === 'aura' && <AuraView />}
      {subtab === 'samtal' && <SamtalFokusTraner />}
      {subtab === 'reading' && <ReadingFocusView />}
      {subtab === 'journal' && <JournalView />}
      {subtab === 'lessons' && <LessonsView lessons={lessons} />}
    </div>
  );
}
